<template>
  <div class="redraw-information">
    <div v-if="isMobile" class="redraw-information__header">
      <div class="redraw-information__header-title">How it works</div>
      <div class="redraw-information__close-button" @click="close">
        <UseSvg class="redraw-information__close-button-icon" icon="menu-close" />
      </div>
    </div>
    <div class="redraw-information__item redraw-information__item--before">
      <div class="redraw-information__item-header">Original Upload</div>
      <img class="redraw-information__image" :src="beforeImageUrl" />
      <div class="redraw-information__content">
        <strong>Sample message to designer:</strong>
        <br />
        Please remove the pink background, change the ribbon color to blue, remove freckles. Change
        “It's a girl!” to Emma.
      </div>
    </div>
    <div class="redraw-information__arrow">
      <UseSvg class="redraw-information__arrow-icon" icon="arrow-right-long" />
    </div>
    <div class="redraw-information__item redraw-information__item--with-list">
      <div class="redraw-information__item-header">Designer Perfected</div>
      <img class="redraw-information__image" :src="afterImageUrl" />
      <div class="redraw-information__content">
        <div class="redraw-information__list">
          <div class="redraw-information__list-item">
            <span class="redraw-information__list-item-number">1</span> Ribbon color changed
          </div>
          <div class="redraw-information__list-item">
            <span class="redraw-information__list-item-number">2</span> Freckles removed
          </div>
          <div class="redraw-information__list-item">
            <span class="redraw-information__list-item-number">3</span> Background removed
          </div>
          <div class="redraw-information__list-item">
            <span class="redraw-information__list-item-number">4</span> Text changed
          </div>
        </div>
      </div>
    </div>
    <button v-if="!isMobile" class="redraw-information__close-button" type="button" @click="close">
      <UseSvg class="redraw-information__close-button-icon" icon="menu-close" />
    </button>
  </div>
</template>

<script>
import { remoteImageStaticUrl } from "common/utils/remote-image";
import UseSvg from "common/vue/components/UseSvg.vue";

export default {
  name: "RedrawInformation",
  components: {
    UseSvg
  },
  props: {
    isMobile: {
      type: Boolean,
      default: false
    }
  },
  emits: ["close"],
  computed: {
    beforeImageUrl() {
      return remoteImageStaticUrl("gen/redraw-info/before-v2.png", {
        ixlib: "rb-0.3.5",
        h: 275,
        dpr: 2,
        q: 60
      });
    },
    afterImageUrl() {
      return remoteImageStaticUrl("gen/redraw-info/after-v2.png", {
        ixlib: "rb-0.3.5",
        h: 275,
        dpr: 2,
        q: 60
      });
    }
  },
  methods: {
    close() {
      this.$emit("close");
    }
  }
};
</script>

<style lang="scss" scoped>
@import "frontend/init/jiffy/settings.scss";
@import "frontend/init/jiffy/mixin.scss";

.redraw-information {
  $this: &;

  display: flex;
  background-color: $white;
  padding: 20px;
  box-shadow: 0px 15px 20px 0px rgba($black, 0.04);
  border: 1px solid $crafterpay-gray-modern-200;
  border-radius: 8px;

  @include only-mobile {
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 0;
    width: 100vw;
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 5px 20px;

    #{$this}__close-button {
      position: static;
      margin-right: -5px;
    }

    #{$this}__close-button-icon {
      height: 25px;
      width: 25px;
    }

    @include only-mobile {
      padding: 8px 13px 5px 13px;
    }
  }

  &__header-title {
    color: $crafterpay-gray-modern-800;
    font-size: 16px;
    font-weight: 600;
    line-height: 18px;
    text-align: left;
  }

  &__arrow {
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: $black;
    border-radius: 50%;
    width: 48px;
    height: 48px;
    position: absolute;
    right: 50%;
    top: 30%;
    transform: translate(65%, -50%);

    @include only-mobile {
      width: 32px;
      height: 32px;
      top: 53%;
      transform: rotate(90deg) translate(-15%, -50%);
    }
  }

  &__arrow-icon {
    color: $white;
    width: 26px;
    height: 26px;
    flex-shrink: 0;

    @include only-mobile {
      width: 14px;
      height: 14px;
    }
  }

  &__item {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    gap: 15px;
    border-radius: 8px;
    padding: 15px;
    width: 300px;

    &:first-child {
      margin-right: 15px;
    }

    &--before {
      background-color: $crafterpay-gray-modern-100;
    }

    &--with-list {
      padding: 15px 10px;
    }

    @include only-mobile {
      width: 94%;

      &:first-child {
        margin-right: 0;
        margin-top: 30px;
      }
    }
  }

  &__item-header {
    color: $crafterpay-gray-modern-800;
    font-size: 18px;
    font-weight: 600;
    line-height: 20px;
    text-align: left;

    @include only-mobile {
      margin-top: 8px;
    }
  }

  &__image {
    height: 275px;
    width: auto;
    object-fit: contain;

    @include only-mobile {
      height: 200px;
    }
  }

  &__content {
    color: $crafterpay-gray-modern-600;
    font-size: 15px;
    line-height: 17px;
    text-align: left;
    font-weight: 400;

    & > strong {
      color: $crafterpay-gray-modern-800;
    }

    @include only-mobile {
      margin-bottom: 10px;
    }
  }

  &__list {
    display: grid;
    grid-template-columns: 1fr;
    gap: 8px;
    width: 100%;

    @include only-mobile {
      grid-template-columns: 1fr 1fr;
    }
  }

  &__list-item {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 5px;
    color: $crafterpay-gray-modern-800;
    font-size: 15px;
    line-height: 1;
    text-align: left;

    @include only-mobile {
      font-size: 14px;
    }
  }

  &__list-item-number {
    color: $white;
    font-size: 15px;
    line-height: 17px;
    background-color: $green-emerald-small-light;
    border-radius: 50%;
    padding: 5px;
    width: 20px;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;

    @include only-mobile {
      font-size: 14px;
      width: 16px;
      height: 16px;
    }
  }

  &__close-button {
    position: absolute;
    padding: 0;
    background-color: transparent;
    color: $text-black;
    border-radius: 50%;
    border-style: none;
    right: 8px;
    top: 8px;
    width: 25px;
    height: 25px;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 5;
  }

  &__close-button-icon {
    width: 20px;
    height: 20px;
  }
}
</style>
